import { QuestionnaireType, useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import type { ResponderTargetPair as RequestResponderTargetPair } from '@app/src/pages/ResourceCollection/Collections/Product/CreateNewProductRequestModal'
import type { QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { Dialog } from '@mui/material'
import React, { useEffect, useState } from 'react'
import CompanyQuestionnaire from './CompanyQuestionnaire'
import type { Periods } from './PeriodSelector/PeriodSelector'
import RequestType from './RequestType'
import SfdrReportWithCompanies from './SfdrReport/SfdrReportWithCompanies'
import SfdrReportWithoutCompanies from './SfdrReport/SfdrReportWithoutCompanies'
import WfStandardQuestionnaire from './WfStandardQuestionnaire'

export type CreateNewRequestModalProps = {
  open: boolean
  onClose: () => void
  questionnaireType?: QuestionnaireType
  disableInitialBack?: boolean
  selectedQuestionnaireTemplates?: QuestionnaireTemplate[] | undefined
}

export type BaseFormData = {
  templateIds: Array<{ id: number; title: string }>
  dateType: Periods
  datePeriod: undefined
  dateYear: undefined
  periodName: string
  responders: Array<{ id: number; name: string; organizationId: number }>
  deadline: string
  responderTargetPairs?: RequestResponderTargetPair[]
}

const CreateNewRequestModal: React.FC<CreateNewRequestModalProps> = ({
  open,
  onClose,
  questionnaireType: questionnaireTypeProp = QuestionnaireType.NONE,
  disableInitialBack = false,
  selectedQuestionnaireTemplates,
}) => {
  const { setActiveStep } = useCreationModalProgress()
  const [questionnaireType, setQuestionnaireType] = useState<QuestionnaireType>(questionnaireTypeProp)

  const getQuestionnaireTypeComponent = (questionnaireType: QuestionnaireType) => {
    switch (questionnaireType) {
      case QuestionnaireType.COMPANY:
        return <CompanyQuestionnaire onClose={onClose} onBack={() => setQuestionnaireType(QuestionnaireType.NONE)} />
      case QuestionnaireType.SFDR_WITHOUT_COMPANIES:
        return (
          <SfdrReportWithoutCompanies
            onClose={onClose}
            onBack={() => setQuestionnaireType(QuestionnaireType.NONE)}
            disableInitialBack={disableInitialBack}
          />
        )
      case QuestionnaireType.SFDR_WITH_COMPANIES:
        return (
          <SfdrReportWithCompanies
            onClose={onClose}
            onBack={() => setQuestionnaireType(QuestionnaireType.NONE)}
            disableInitialBack={disableInitialBack}
          />
        )
      case QuestionnaireType.WF:
        return (
          <WfStandardQuestionnaire
            onClose={onClose}
            onBack={() => setQuestionnaireType(QuestionnaireType.NONE)}
            disableInitialBack={disableInitialBack}
            selectedQuestionnaireTemplates={selectedQuestionnaireTemplates}
          />
        )
      case QuestionnaireType.NONE:
        return <RequestType onClose={onClose} setQuestionnaireType={setQuestionnaireType} />
    }
  }

  useEffect(() => {
    if (open && !questionnaireTypeProp) {
      setQuestionnaireType(QuestionnaireType.NONE)
      setActiveStep(1)
    }
    if (open && QuestionnaireType.WF) {
      setActiveStep(1)
    }
  }, [open, questionnaireTypeProp])

  return (
    <Dialog open={open} fullScreen onClose={onClose}>
      {getQuestionnaireTypeComponent(questionnaireType)}
    </Dialog>
  )
}

export default CreateNewRequestModal
